import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth-store';
import { Activity, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

// Always-visible system health dot for the AppShell header. Refetches every
// 15s. Operator sees green/yellow/red at a glance + tooltip detail with the
// component statuses + last data ages. Drives operator confidence: "is the
// data I'm looking at fresh, or is everything frozen?"

const REFRESH_MS = 15_000;

function statusColor(s: 'ok' | 'warn' | 'error'): string {
  if (s === 'ok') return 'bg-positive';
  if (s === 'warn') return 'bg-warning';
  return 'bg-destructive';
}

function statusLabel(s: 'ok' | 'warn' | 'error'): string {
  if (s === 'ok') return 'Sistema operativo';
  if (s === 'warn') return 'Funcionando con avisos';
  return 'Sistema con problemas';
}

function fmtAge(sec: number | null): string {
  if (sec === null) return 'sin datos';
  if (sec < 60) return `hace ${sec}s`;
  if (sec < 3600) return `hace ${Math.floor(sec / 60)}m`;
  return `hace ${Math.floor(sec / 3600)}h`;
}

function StatusIcon({ status }: { status: 'ok' | 'warn' | 'error' }) {
  if (status === 'ok') return <CheckCircle2 className="size-3 text-positive" />;
  if (status === 'warn') return <AlertTriangle className="size-3 text-warning" />;
  return <XCircle className="size-3 text-destructive" />;
}

export function HealthPill() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const health = trpc.system.health.useQuery(undefined, {
    enabled: isAuthenticated,
    refetchInterval: REFRESH_MS,
    staleTime: REFRESH_MS,
  });

  if (!isAuthenticated || !health.data) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs"
        data-testid="health-pill-loading"
      >
        <span className="size-2 rounded-full bg-muted-foreground" />
        <span className="text-muted-foreground">…</span>
      </span>
    );
  }

  const { overall, checks } = health.data;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid="health-pill"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs hover:bg-popover focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <span className={cn('size-2 rounded-full', statusColor(overall))} />
            <Activity className="size-3 text-muted-foreground" />
            <span className="font-mono text-muted-foreground">
              {fmtAge(checks.fixtureSync.ageSec)}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-xs">
          <div className="space-y-1.5 text-xs">
            <p className="font-semibold text-foreground">{statusLabel(overall)}</p>
            <Row label="Base de datos" status={checks.db.status} detail="" />
            <Row label="Cache (Redis)" status={checks.redis.status} detail="" />
            <Row
              label="Última cuota recibida"
              status={checks.fixtureSync.status}
              detail={fmtAge(checks.fixtureSync.ageSec)}
            />
            <Row
              label="Último cambio del scanner"
              status={checks.scanner.status}
              detail={fmtAge(checks.scanner.ageSec)}
            />
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Row({
  label,
  status,
  detail,
}: {
  label: string;
  status: 'ok' | 'warn' | 'error';
  detail: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5">
        {detail && <span className="font-mono">{detail}</span>}
        <StatusIcon status={status} />
      </span>
    </div>
  );
}
